import { useMemo } from 'react';
import { ELIGIBILITY_UNIVERSE_CODE_ENUM } from '../../../../../constants/hudEligibilityUniverseCodes';
import messages from './messages';

export const EMPTY_CONTRACT = {
  id: null,
  contractNumber: null,
  eligibilityUniverseCode: null,
  effectiveDate: null,
  expirationDate: null,
  minimumRent: null,
};

const PRIMARY_FIELD_PREFIX = 'HUDContract';
const SECONDARY_FIELD_PREFIX = 'HUDSecondaryContract';

/**
 * Syncs the legacy flat setup fields from the first contract in the given array.
 *
 * This is needed to maintain compatibility with existing code that relies on the
 * old flat fields, while allowing the new array-based structure for multiple contracts.
 *
 * TODO remove sync function when legacy fields are dropped from database and codebase
 * and there are no longer any dependencies on these fields.
 *
 * @param {Function} onChange
 * @param {Array} contracts
 * @param {boolean} isPrimary - true for primary, false for secondary
 */
const syncLegacyFields = (onChange, contracts, isPrimary) => {
  const prefix = isPrimary ? PRIMARY_FIELD_PREFIX : SECONDARY_FIELD_PREFIX;
  const firstContract = contracts?.[0] ?? EMPTY_CONTRACT;
  onChange({
    target: {
      name: `setup.${prefix}Number`,
      value: firstContract.contractNumber ?? null,
    },
  });
  onChange({
    target: {
      name: `setup.${prefix}EligibilityUniverseCode`,
      value: firstContract.eligibilityUniverseCode ?? null,
    },
  });
  onChange({
    target: {
      name: `setup.${prefix}EffectiveDate`,
      value: firstContract.effectiveDate ?? null,
    },
  });
  onChange({
    target: {
      name: `setup.${prefix}ExpirationDate`,
      value: firstContract.expirationDate ?? null,
    },
  });
};

export const useHUDSubsidyTypeSectionHandlers = ({
  intl,
  onChange,
  primaryContracts,
  secondaryContracts,
}) => {
  const eligibilityUniverseCodeOptions = useMemo(
    () => [
      {
        value: ELIGIBILITY_UNIVERSE_CODE_ENUM.PRE_1981,
        text: intl.formatMessage(messages.eligibilityUniverseCodePre1981),
      },
      {
        value: ELIGIBILITY_UNIVERSE_CODE_ENUM.POST_1981,
        text: intl.formatMessage(messages.eligibilityUniverseCodePost1981),
      },
    ],
    [intl],
  );

  const handlePrimaryContractChange = (index, updatedContract) => {
    const updated = primaryContracts.map((c, i) =>
      i === index ? updatedContract : c,
    );
    onChange({ target: { name: 'setup.primaryContracts', value: updated } });
    syncLegacyFields(onChange, updated, true);
  };

  const handleRemovePrimaryContract = (index) => {
    const updated = primaryContracts.filter((_, i) => i !== index);
    onChange({ target: { name: 'setup.primaryContracts', value: updated } });
    syncLegacyFields(onChange, updated, true);
  };

  const handleAddPrimaryContract = () => {
    const updated = [...primaryContracts, { ...EMPTY_CONTRACT }];
    onChange({ target: { name: 'setup.primaryContracts', value: updated } });
    syncLegacyFields(onChange, updated, true);
  };

  const handleSecondaryContractChange = (index, updatedContract) => {
    const updated = secondaryContracts.map((c, i) =>
      i === index ? updatedContract : c,
    );
    onChange({ target: { name: 'setup.secondaryContracts', value: updated } });
    syncLegacyFields(onChange, updated, false);
  };

  const handleRemoveSecondaryContract = (index) => {
    const updated = secondaryContracts.filter((_, i) => i !== index);
    onChange({ target: { name: 'setup.secondaryContracts', value: updated } });
    syncLegacyFields(onChange, updated, false);
  };

  const handleAddSecondaryContract = () => {
    const updated = [...secondaryContracts, { ...EMPTY_CONTRACT }];
    onChange({ target: { name: 'setup.secondaryContracts', value: updated } });
    syncLegacyFields(onChange, updated, false);
  };

  return {
    eligibilityUniverseCodeOptions,
    handleAddPrimaryContract,
    handlePrimaryContractChange,
    handleRemovePrimaryContract,
    handleAddSecondaryContract,
    handleSecondaryContractChange,
    handleRemoveSecondaryContract,
  };
};
